import React, { memo, useEffect, useCallback, useState } from 'react';
import styled from 'styled-components';
import { Title2, Headline } from '../../../components/Typography';
import {
  ORDER_REFUNDED,
  ORDER_REJECTED,
  ORDER_SUBMITTED,
  ORDER_ACCEPTED,
  ORDER_COMPLETE,
  ORDER_ON_DELIVERY,
  ORDER_READY_TO_PICKUP,
} from '../orderStatusTypes';
import { withStyles } from '@material-ui/core';
import Dialog from '../../../components/Dialog/Dialog';
import ProductPreview from './ProductPreview';
import Receipt from './Receipt';
import PaymentMethod from './PaymentMethod';
import moment from 'moment';
import Icon from './Icon';
import TrackButton from './TrackButton';
import CloseButton from '../../../components/CloseButton/CloseButton';
import calculateOrderReadyTime from '../utils/calculateOrderReadyTime';
import StepsSummary from './StepsSummary';
import appConfig from '../../../../app-config';
const styles = () => ({
  scrollPaper: {},
  paper: {
    borderRadius: '10px',
  },
});
const Wrapper = styled.div`
  display: grid;
`;
const ContentWrapper = styled.div``;
const Content = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  height: ${(props) => (props.mini ? '4em' : '7em')};
  @media (max-width: 480px) {
    height: 5em;
  }
`;
const TitleWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  grid-gap: 10px;
  align-items: center;
`;
const Title = styled(Title2)`
  color: ${(props) => props.theme.orderStatusTitleColor};
  margin-bottom: 10px;
`;
const TitleNote = styled.div`
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: 0.02px;
  text-align: right;
  color: #737a87;
`;
const TitleCardWrapper = styled.div`
  display: grid;
  grid-template-columns: 0.7fr 0.3fr;
`;

const DetailsWrapper = styled.div`
  padding: 2rem 24px 50px 24px;
  display: grid;
  grid-gap: 32px;
`;
const DetailWrapper = styled.div`
  display: grid;
  grid-gap: 22px;
`;
const DetailTitle = styled(Headline)`
  font-weight: 600;
  width: max-content;
  :after {
    content: '';
    display: block;
    width: calc(100% + 2px);

    border-bottom: ${(props) => props.categoriesBarSelectedBorderColorWidth}
      solid ${(props) => props.theme.orderStatusTitleBarBorderColor};
  }
`;
const ReceiptWrapper = styled.div`
  // margin: auto;
  display: flex;
  justify-content: flex-end;
`;

const NoteWraper = styled.div`
  width: 100%;
  margin: 0 5px 20px 0;
`;

const NoteText = styled.span`
  width: 100%;
  height: 17px;
  white-space: pre-line;
  margin: 0 0 0 4px;
  font-family: Inter;
  font-size: 14px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: 0.02px;
  color: var(--colors-basic-dark);
`;
const Line = styled.hr`
  width: 100%;
  color: #c1c1c1;
  margin: 0;
`;

const StatusCardWrapper = styled.div`
  padding: 32px 16px 32px 24px;
  height: 17rem;
  border-radius: 10px;
  background: ${(props) => props.theme.orderStatusBackgroundColor};
  cursor: ${(props) => !props.isForModal && 'pointer'};
  justify-content: center;
  &:hover {
    background: ${(props) =>
      !props.isForModal && props.theme.orderStatusBackgroundColorHover};
    transition: all 0.25s ease;
  }

  @media (max-width: 480px) {
    height: 12rem;
  }
`;

const OrderStatusMessage = styled.div`
  font-size: 16px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  font-family: ${(props) => props.font || ''};
  color: ${(props) => props.customColor || 'black'};
`;

const BarContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  width: 100%;
  height: 8px;
  position: relative;
`;

const ProgressBar = styled.div`
  height: 10px;
  margin-left: 5px;
  border-right: 10px solid ${(props) => props.theme.orderStatusBackgroundColor};
  width: 100%;

  ${(props) =>
    props.animate
      ? `
  animation: load 4s infinite linear;
  background: repeating-linear-gradient(
      45deg,
      transparent 0 6px,
      rgba(0, 0, 0, 0.1) 6px 12px
    ),
    linear-gradient(#b0d0ff, #2e82ff);
  background-size: calc(12px / 0.707) 100%, 100% 800%; /*cos(45deg) == 0.707*/
  background-position: inherit;

  @keyframes load {
    0% {
      background-position: 0 0;
    }
    100% {
      background-position: calc(10 * (12px / 0.707)) 100%;
    }
  }
  `
      : `background: #b4c3d8;`}
`;
const CardContent = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin-top: ${(props) => (props.small ? '2.5em' : '3.5em')};
  margin-bottom: 20px;
`;
const OrderStatusBody = ({
  orderType,
  status,
  businessName,
  orderReadyTime,
  deliveryTrackingURL,
  delay,
  index,
  displayComplete,
  statusMessage,
}) => {
  return (
    <ContentWrapper>
      <CardContent small={window.innerWidth < 700}>
        {status === ORDER_COMPLETE && displayComplete ? (
          <Content>
            <Icon className='fas fa-badge-check' />
          </Content>
        ) : status === ORDER_REFUNDED || status === ORDER_REJECTED ? (
          <Content>
            <Icon className='fas fa-exclamation-triangle' />
          </Content>
        ) : (
          <BarContainer>
            <ProgressBar
              animate={status === ORDER_SUBMITTED || ORDER_ACCEPTED}
            />
            <ProgressBar
              animate={
                status === ORDER_ACCEPTED ||
                status === ORDER_COMPLETE ||
                status === ORDER_ON_DELIVERY
              }
            />
            <ProgressBar
              animate={
                status === ORDER_COMPLETE || status === ORDER_ON_DELIVERY
              }
            />
          </BarContainer>
        )}
      </CardContent>
      <OrderStatusMessage
        customColor={appConfig.styleConfig.orderStatusTitleColor}
        font={appConfig.styleConfig.orderStatusMessageFont}
      >
        {status === ORDER_SUBMITTED ? (
          <>Waiting for restaurant confirmation…</>
        ) : status === ORDER_ACCEPTED && delay === 0 ? (
          <>Your order is being prepared</>
        ) : status === ORDER_ACCEPTED && delay > 0 ? (
          <>
            Your order is being prepared. Please contact the store for more
            information.
          </>
        ) : status === ORDER_READY_TO_PICKUP &&
          orderType === 'pickup' &&
          !displayComplete ? (
          <>{`at ${businessName}`}</>
        ) : status === ORDER_ON_DELIVERY &&
          orderType === 'delivery' &&
          !displayComplete ? (
          <>
            {deliveryTrackingURL ? (
              <div style={{ marginTop: '25px' }}>
                <TrackButton deliveryTrackingURL={deliveryTrackingURL} />
              </div>
            ) : (
              <div>{`Your order is out for  delivery`}</div>
            )}
          </>
        ) : status === ORDER_REFUNDED || status === ORDER_REJECTED ? (
          <div>{statusMessage}</div>
        ) : status === ORDER_COMPLETE && displayComplete ? (
          <div>Your order is complete!</div>
        ) : null}
      </OrderStatusMessage>
    </ContentWrapper>
  );
};
const renderTitle = (
  status,
  orderType,
  orderReadyTime,
  deliveryTrackingURL,
  displayComplete
) => {
  if (status === ORDER_SUBMITTED) {
    return <Title id='OrderStatus-Title'>Order placed</Title>;
  } else if (status === ORDER_ACCEPTED && orderType === 'pickup') {
    return (
      <TitleCardWrapper id='OrderStatus-Title'>
        <Title id='OrderStatus-Title'>{orderReadyTime}</Title>
        <TitleNote>Estimated pickup</TitleNote>
      </TitleCardWrapper>
    );
  } else if (status === ORDER_ACCEPTED && orderType === 'delivery') {
    return (
      <TitleWrapper id='OrderStatus-Title'>
        <Title id='OrderStatus-Title'>{orderReadyTime}</Title>
        <TitleNote>Estimated arrival</TitleNote>
      </TitleWrapper>
    );
  } else if (
    status === ORDER_READY_TO_PICKUP &&
    orderType === 'pickup' &&
    !displayComplete
  ) {
    return <Title id='OrderStatus-Title'>Ready for pickup</Title>;
  } else if (
    (status === ORDER_ON_DELIVERY && orderType === 'delivery',
    !deliveryTrackingURL && !displayComplete)
  ) {
    return (
      <TitleCardWrapper id='OrderStatus-Title'>
        <Title id='OrderStatus-Title'>{orderReadyTime}</Title>
        <TitleNote>Estimated arrival</TitleNote>
      </TitleCardWrapper>
    );
  } else if (
    (status === ORDER_ON_DELIVERY && orderType === 'delivery',
    deliveryTrackingURL && !displayComplete)
  ) {
    return <Title id='OrderStatus-Title'>Out for delivery</Title>;
  } else {
    return <Title id='OrderStatus-Title'></Title>;
  }
};
const OrderStatusModal3 = ({
  orderStatusModalData,
  setOrderStatusModalData,
  classes,
  orderDelayMessage,
  currency,
  is_tip_enabled,
  displayComplete,
  locations,
}) => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const updatePhoneNumber = useCallback(() => {
    if (orderStatusModalData) {
      const businessId = orderStatusModalData?.business_id;
      const selectedBusiness = locations?.find(
        (business) => business.id === businessId
      );
      if (selectedBusiness) {
        setPhoneNumber(selectedBusiness.phone);
      }
    }
  }, [orderStatusModalData]);
  useEffect(() => {
    updatePhoneNumber();
  }, [updatePhoneNumber]);
  if (!orderStatusModalData) return null;
  const status = orderStatusModalData?.status;
  const businessName = orderStatusModalData?.business_name;
  const orderType = orderStatusModalData?.order_type;
  const orderReadyTime = calculateOrderReadyTime(orderStatusModalData, false);
  const deliveryTrackingURL = orderStatusModalData?.delivery_tracking_url;
  const delay = orderStatusModalData?.delay;
  const statusMessage = orderStatusModalData?.status_change_message
    ? orderStatusModalData?.status_change_message
    : ' Unfortunately, we cannot fulfill your order at this time. Your order will be refunded.';
  return (
    <Dialog
      open={orderStatusModalData}
      classes={classes}
      onBackdropClick={() => setOrderStatusModalData(null)}
      scroll='body'
      maxWidth={'md'}
      fullWidth={true}
    >
      <Wrapper>
        <StatusCardWrapper>
          <TitleWrapper>
            {renderTitle(
              status,
              orderType,
              orderReadyTime,
              deliveryTrackingURL,
              displayComplete
            )}
            <div>
              <CloseButton
                id='OrderStatusModal-SetOrderStatus'
                onClick={() => setOrderStatusModalData(null)}
              />
            </div>
          </TitleWrapper>

          <OrderStatusBody
            orderType={orderType}
            status={status}
            businessName={businessName}
            orderReadyTime={orderReadyTime}
            deliveryTrackingURL={deliveryTrackingURL}
            delay={delay}
            displayComplete={displayComplete}
            statusMessage={statusMessage}
          />
        </StatusCardWrapper>
        <DetailsWrapper>
          {status !== ORDER_REFUNDED && status !== ORDER_REJECTED && (
            <StepsSummary
              orderStatusModalData={orderStatusModalData}
              orderReadyTime={orderReadyTime}
              phoneNumber={phoneNumber}
              displayComplete={displayComplete}
            />
          )}

          <DetailWrapper>
            <DetailTitle id='OrderStatusModal-OrderSummary'>
              Your Items
            </DetailTitle>
            <ProductPreview
              products={orderStatusModalData?.flatProducts}
              isForPreview
            />
          </DetailWrapper>
          {orderStatusModalData?.special_instruction && (
            <DetailWrapper>
              <DetailTitle id='OrderStatusModal-OrderSummary'>
                Order Instructions
              </DetailTitle>
              <NoteWraper>
                <NoteText>{orderStatusModalData?.special_instruction}</NoteText>
              </NoteWraper>
              <Line size='2' />
            </DetailWrapper>
          )}
          <DetailWrapper>
            {/* <DetailTitle>Order Summary</DetailTitle> */}
            <ReceiptWrapper>
              <Receipt
                id='OrderStatusModal-Receipt'
                orderSummary={{
                  ...orderStatusModalData?.order_summary,
                  currency: orderStatusModalData?.currency
                    ? orderStatusModalData?.currency
                    : currency,
                  orderMethodType: orderStatusModalData?.order_type,
                  serviceFees: orderStatusModalData?.service_fees,
                }}
                is_tip_enabled={is_tip_enabled}
              />
            </ReceiptWrapper>
          </DetailWrapper>
          {orderStatusModalData?.order_summary?.total > 0 ? (
            <DetailWrapper>
              <DetailTitle id='OrderStatusModal-PaymentMethod'>
                Payment Method
              </DetailTitle>
              <PaymentMethod
                paymentMethod={orderStatusModalData?.payments[0]}
              />
            </DetailWrapper>
          ) : null}
        </DetailsWrapper>
      </Wrapper>
    </Dialog>
  );
};

export default withStyles(styles)(memo(OrderStatusModal3));
